import { ObjectUtil } from 'helper-util';
import { addBusinessDays, addBusinessMonths, addBusinessWeeks } from '../../utils/dateUtil';
import { FIELD, SettlementDateByCurrency } from './OrderEntryFormConstants';
import moment from 'moment';

export const getClientApprovalPayload = (formValues, basketUploadPayload = null) => {
  return {
    counterParty: formValues.counterparty.value.id,
    tradeDate: new Date(formValues.tradeDate.value),
    settlementDate: new Date(formValues.settlementDate.value),
    maturityDate: new Date(formValues.maturityDate.value),
    terminationDate: new Date(formValues.terminationDate.value),
    basketId: formValues.basketSelect.value.id,
    basketVersion: formValues.basketSelect.value.version,
    clientId: formValues.client.value.label,
    swapName: formValues.swapName.value,
    swapAttributes: {
      notionalAmount: formValues.notional.value,
      currency: formValues.swapCurrency.value.id,
      direction: formValues.swapDirection.value.id,
      benchmark: formValues.benchmark.value.id,
      returnType: formValues.returnType.value.label,
      actingAs: formValues.actingAs.value.label,
      priceType: formValues.priceType.value.label,
      subRights: formValues.subRights.value.label,
      breakRights: formValues.breakRights.value.label,
      resetFrequency: formValues.resetFrequency.value.label,
      maturity: formValues.maturity.value.label,
      fixingOffset: formValues.fixingOffset.value.label,
      underLyingInstrument: formValues.underLyingInstrument.value,
      fixedRate: formValues.fixedRate.value,
      spread: formValues.spread.value,
      orderComments: formValues.orderComments.value,
      paymentOffset: formValues.paymentOffset.value.label,
      dividendPayment: formValues.dividendPayment.value.label,
      subPayments: formValues.subPayments.value.label,
      // margin:
      //   formValues.benchmark.value.id === 'FIXED'
      //     ? formValues.fixedRate.value.id
      //     : formValues.fixingOffset.value.id,
    },

    basket: basketUploadPayload,
  };
};

const calculateDates = (
  tradeDate,
  maturityDayCount: number = 1,
  offsetDayCount: number = 1,
  holidays,
) => {
  const settlementDate = addBusinessDays(tradeDate, offsetDayCount, holidays);
  let maturityDate = null;
  if (maturityDayCount >= 30) {
    maturityDate = addBusinessMonths(tradeDate, maturityDayCount / 30, holidays);
  } else {
    maturityDate = addBusinessWeeks(tradeDate, maturityDayCount / 7, holidays);
  }
  const terminationDate = addBusinessDays(maturityDate, offsetDayCount, holidays);
  return { settlementDate, maturityDate, terminationDate };
};

const setCurrentValueInForm = (field, value, setFormValues) => {
  return setFormValues((prev) => {
    return {
      ...prev,
      [field.id]: { ...prev[field.id], value, isTouched: true },
    };
  });
};

const getMaturityDayCount = (maturityValue: string) => {
  let maturityDayCount: number = 1;
  switch (maturityValue) {
    // case 'Settlement Date + 1':
    //   maturityDayCount = 1;
    //   break;
    case '1week':
      maturityDayCount = 7;
      break;
    case '2week':
      maturityDayCount = 14;
      break;
    case '3week':
      maturityDayCount = 21;
      break;
    case '1Month':
      maturityDayCount = 30;
      break;
    case '2Month':
      maturityDayCount = 60;
      break;
    case '3Month':
      maturityDayCount = 90;
      break;
    case '6Month':
      maturityDayCount = 180;
      break;
    case '9Month':
      maturityDayCount = 270;
      break;
    case '1Year':
      maturityDayCount = 365;
      break;
  }
  return maturityDayCount;
};

const getPaymentOffsetCount = (paymentOffsetValue: string) => {
  let paymentOffsetCount: number = 1;
  switch (paymentOffsetValue) {
    case '0d':
      paymentOffsetCount = 0;
      break;
    case '1d':
      paymentOffsetCount = 1;
      break;
    case '2d':
      paymentOffsetCount = 2;
      break;
    case '3d':
      paymentOffsetCount = 3;
      break;
  }
  return paymentOffsetCount;
};

export const getSettlementCycleCount = (swapCurrency: string) => {
  return SettlementDateByCurrency?.find((c) => swapCurrency === c.currency)?.incrementBy || 1;
};

const setDates = (
  field,
  value,
  maturityDayCount,
  incrementedBy,
  formValues,
  setFormValues,
  holidays,
) => {
  let { settlementDate, maturityDate, terminationDate } = calculateDates(
    field.id === FIELD.TRADE_DATE ? value : formValues[FIELD.TRADE_DATE].value,
    maturityDayCount,
    incrementedBy,
    holidays,
  );
  const tradeDate = formValues[FIELD.TRADE_DATE].value;
  const maturityDropDownValue = formValues[FIELD.MATURITY].value;
  const settlementMinDate = addBusinessDays(tradeDate, incrementedBy, holidays);
  let terminationMinDate = addBusinessDays(maturityDate, incrementedBy, holidays);

  const settlePlusOneMaturitySelected =
    value && value.id === 'Settlement Date + 1'
      ? true
      : field.id !== FIELD.MATURITY &&
          maturityDropDownValue &&
          maturityDropDownValue.id === 'Settlement Date + 1'
        ? true
        : false;

  if (settlePlusOneMaturitySelected) {
    maturityDate = addBusinessDays(settlementDate, 1, holidays);
    terminationDate = addBusinessDays(maturityDate, incrementedBy, holidays);
    terminationMinDate = addBusinessDays(maturityDate, incrementedBy, holidays);
  }

  setFormValues((prev) => {
    return {
      ...prev,
      [FIELD.SETTLEMENT_DATE]: {
        ...prev[FIELD.SETTLEMENT_DATE],
        value: settlementDate,
        isTouched: true,
        minDate: settlementMinDate,
        maxDate: maturityDate,
      },
      [FIELD.MATURITY_DATE]: {
        ...prev[FIELD.MATURITY_DATE],
        value: maturityDate,
        isTouched: true,
        minDate: settlePlusOneMaturitySelected
          ? addBusinessDays(settlementDate, 1, holidays)
          : maturityDayCount >= 30
            ? addBusinessMonths(tradeDate, maturityDayCount / 30, holidays)
            : addBusinessWeeks(tradeDate, maturityDayCount / 7, holidays),
      },
      [FIELD.TERMINATION_DATE]: {
        ...prev[FIELD.TERMINATION_DATE],
        value: terminationDate,
        isTouched: true,
        minDate: terminationMinDate,
      },
    };
  });
  setCurrentValueInForm(field, value, setFormValues);
};

export const getIncrementedBy = (field, value, formValues) => {
  const paymentOffsetValue =
    field.id === FIELD.PAYMENTOFFSET ? value.label : formValues[FIELD.PAYMENTOFFSET].value.label;
  const paymentOffsetCount: number = getPaymentOffsetCount(paymentOffsetValue);

  const settlementCycleCount: number = getSettlementCycleCount(
    field.id === FIELD.SWAPCURRENCY ? value.id : formValues[FIELD.SWAPCURRENCY].value.id,
  );

  let incrementedBy = 1;
  if (paymentOffsetValue) {
    incrementedBy = paymentOffsetCount;
  } else {
    incrementedBy = settlementCycleCount;
  }
  return { incrementedBy, settlementCycleCount };
};

export const onFormFieldChange = (field, value, formValues, setFormValues, holidays) => {
  const maturityDayCount: number = getMaturityDayCount(
    field.id === FIELD.MATURITY ? value.label : formValues[FIELD.MATURITY].value.label,
  );
  const { incrementedBy, settlementCycleCount } = getIncrementedBy(field, value, formValues);

  switch (field.id) {
    case FIELD.TRADE_DATE:
      setDates(field, value, maturityDayCount, incrementedBy, formValues, setFormValues, holidays);
      break;

    case FIELD.MATURITY_DATE:
      setDates(field, value, maturityDayCount, incrementedBy, formValues, setFormValues, holidays);
      break;

    case FIELD.MATURITY:
      setDates(field, value, maturityDayCount, incrementedBy, formValues, setFormValues, holidays);
      break;

    // case FIELD.FIXINGOFFSET:
    //   setDates(field, value, maturityDayCount, incrementedBy, formValues, setFormValues, holidays);
    //   break;

    case FIELD.PAYMENTOFFSET:
      setDates(field, value, maturityDayCount, incrementedBy, formValues, setFormValues, holidays);
      break;

    case FIELD.SWAPCURRENCY:
      if (!formValues[FIELD.PAYMENTOFFSET].value.label) {
        setDates(
          field,
          value,
          maturityDayCount,
          incrementedBy,
          formValues,
          setFormValues,
          holidays,
        );
      } else {
        setCurrentValueInForm(field, value, setFormValues);

        const settlementCycleCount: number = getSettlementCycleCount(
          field.id === FIELD.SWAPCURRENCY ? value.id : formValues[FIELD.SWAPCURRENCY].value.id,
        );
        const incrementedBy = settlementCycleCount;
        setDates(
          field,
          value,
          maturityDayCount,
          incrementedBy,
          formValues,
          setFormValues,
          holidays,
        );
      }

      setFormValues((prev) => {
        return {
          ...prev,
          // [FIELD.FIXINGOFFSET]: {
          //   ...prev[FIELD.FIXINGOFFSET],
          //   value: { id: `${settlementCycleCount}d`, label: `${settlementCycleCount}d` },
          //   isTouched: true,
          // },
          [FIELD.PAYMENTOFFSET]: {
            ...prev[FIELD.PAYMENTOFFSET],
            value: { id: `${settlementCycleCount}d`, label: `${settlementCycleCount}d` },
            isTouched: true,
          },
        };
      });
      break;

    default:
      setCurrentValueInForm(field, value, setFormValues);
  }
};

export const initialOrderEntryForm = () => {
  return {
    swapName: { value: '' },
    returnType: { value: '' },
    actingAs: { value: '' },
    client: { value: [] },
    counterparty: { value: '' },
    priceType: { value: '' },
    subRights: { value: '' },
    breakRights: { value: '' },
    resetFrequency: { value: '' },
    maturity: { value: '' },
    swapDirection: { value: '' },
    swapCurrency: { value: '' },
    benchmark: { value: '' },
    spread: { value: 0 },
    fixingOffset: { value: '' },
    fixedRate: { value: '' },
    underLyingInstrument: { value: '' },
    singleSecurity: { value: '' },
    notional: { value: '', isDisabled: true },
    tradeDate: { value: moment() },
    settlementDate: { value: '' },
    maturityDate: { value: '' },
    terminationDate: { value: '' },
    orderComments: { value: '' },
    securitiesList: { value: [] },
    basketUpload: { value: {} },
    basketSelect: { value: '' },
    paymentOffset: { value: '' },
    dividendPayment: { value: '' },
    subPayments: { value: '' },
    timeStamp: { value: '', isDisabled: true },
    clientApproval: { value: null },
    addEditBasketItems: { value: null },
    execute: { value: null, isDisabled: true },
    basketName: { value: '' },
  };
};

export const autoFillOrderDetails = (swapAttributes, setFormValues, orderDetails) => {
  const now = new Date();
  const isoString = now.toISOString();

  setFormValues((prev) => {
    return {
      ...prev,
      ['swapName']: {
        ...prev['swapName'],
        value: orderDetails.swapName,
        isDisabled: true,
      },
      ['returnType']: {
        ...prev['returnType'],
        value: { label: swapAttributes.returnType },
        isDisabled: true,
      },
      ['actingAs']: {
        ...prev['actingAs'],
        value: { label: swapAttributes.actingAs },
        isDisabled: true,
      },
      ['priceType']: {
        ...prev['priceType'],
        value: { label: swapAttributes.priceType },
        isDisabled: true,
      },
      ['subRights']: {
        ...prev['subRights'],
        value: { label: swapAttributes.subRights },
        isDisabled: true,
      },
      ['breakRights']: {
        ...prev['breakRights'],
        value: { label: swapAttributes.breakRights },
        isDisabled: true,
      },
      ['resetFrequency']: {
        ...prev['resetFrequency'],
        value: { label: swapAttributes.resetFrequency },
        isDisabled: true,
      },
      ['maturity']: {
        ...prev['maturity'],
        value: { label: swapAttributes.maturity },
        isDisabled: true,
      },
      ['swapDirection']: {
        ...prev['swapDirection'],
        value: { id: swapAttributes.direction, label: swapAttributes.direction },
        isDisabled: true,
      },
      ['spread']: {
        ...prev['spread'],
        value: swapAttributes.spread,
        isDisabled: true,
      },
      ['fixingOffset']: {
        ...prev['fixingOffset'],
        value: { label: swapAttributes.fixingOffset },
        isDisabled: true,
      },
      ['fixedRate']: {
        ...prev['fixedRate'],
        value: swapAttributes.fixedRate,
        isDisabled: true,
      },
      ['underLyingInstrument']: {
        ...prev['underLyingInstrument'],
        value: swapAttributes.underLyingInstrument,
        isDisabled: true,
      },
      ['singleSecurity']: {
        ...prev['singleSecurity'],
        value: swapAttributes.singleSecurity,
        isDisabled: true,
      },
      ['notional']: {
        ...prev['notional'],
        value: swapAttributes.notionalAmount,
        isDisabled: true,
      },
      ['tradeDate']: {
        ...prev['tradeDate'],
        value: orderDetails.tradeDate,
        isDisabled: true,
      },
      ['settlementDate']: {
        ...prev['settlementDate'],
        value: orderDetails.settlementDate,
        isDisabled: true,
      },
      ['maturityDate']: {
        ...prev['maturityDate'],
        value: orderDetails.maturityDate,
        isDisabled: true,
      },
      ['terminationDate']: {
        ...prev['terminationDate'],
        value: orderDetails.terminationDate,
        isDisabled: true,
      },
      ['orderComments']: {
        ...prev['orderComments'],
        value: swapAttributes.orderComments,
        isDisabled: true,
      },
      ['paymentOffset']: {
        ...prev['paymentOffset'],
        value: { label: swapAttributes.paymentOffset },
        isDisabled: true,
      },
      ['dividendPayment']: {
        ...prev['dividendPayment'],
        value: { label: swapAttributes.dividendPayment },
        isDisabled: true,
      },
      ['subPayments']: {
        ...prev['subPayments'],
        value: { label: swapAttributes.subPayments },
        isDisabled: true,
      },
      ['benchmark']: {
        ...prev['benchmark'],
        value: { label: swapAttributes.benchmark },
        isDisabled: true,
      },
      ['swapCurrency']: {
        ...prev['swapCurrency'],
        value: { id: swapAttributes.currency, label: swapAttributes.currency },
        isDisabled: true,
      },
      ['client']: {
        ...prev['client'],
        value: { label: orderDetails.clientId },
        isDisabled: true,
      },
      ['counterparty']: {
        ...prev['counterparty'],
        value: { label: orderDetails.counterParty },
        isDisabled: true,
      },
      ['clientApproval']: {
        ...prev['clientApproval'],
        value: null,
        isDisabled: true,
      },
      ['timeStamp']: {
        ...prev['timeStamp'],
        value: isoString,
        isDisabled: false,
      },
      ['execute']: {
        ...prev['execute'],
        value: null,
        isDisabled: false,
      },
      ['basketSelect']: {
        ...prev['basketSelect'],
        value: {
          id: orderDetails?.currentBasket?.basketId,
          label: orderDetails?.currentBasket?.basketName,
          version: orderDetails?.currentBasket?.version,
        },
        isDisabled: true,
      },
      ['basketName']: {
        ...prev['basketName'],
        value: orderDetails?.currentBasket?.basketName,
        isDisabled: true,
      },
    };
  });
};

export const eachNotional = (price: number, quantity: number, fxRate: number) => {
  return (price ?? 0) * (quantity ?? 0) * (fxRate ?? 1);
};
